/**
 * Application router — the single top-level <Routes> tree for the whole app.
 * Composes the scope-tier route groups (root-stack, customer, site-group,
 * site, staff support) and the build-flag-gated feature bundles (billing,
 * admin, sales, services) into one router, and owns the app-wide navigation
 * side effects (analytics page-view tracking, comma stripping). Each tier /
 * bundle lives in its own module under router/routes/; this file only orders
 * and renders them.
 *
 * Contains:
 * - ApplicationRouter — the routed app shell (default export)
 */
import React, { Suspense, useEffect } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { initGoogleAnalytics, trackPageView } from '@sophocles/analytics';
import App from '@sophocles/components/App';
import RouteLoader from '@sophocles/components/App/RouteLoader';
import { adminRoutes } from '@sophocles/router/routes/admin';
import { billingRoutes } from '@sophocles/router/routes/billing';
import { customerRoutes } from '@sophocles/router/routes/customer';
import { rootStackRoutes } from '@sophocles/router/routes/rootStack';
import { salesRoutes } from '@sophocles/router/routes/sales';
import { servicesRoutes } from '@sophocles/router/routes/services';
import { siteRoutes } from '@sophocles/router/routes/site';
import { siteGroupRoutes } from '@sophocles/router/routes/siteGroup';
import { supportRoutes } from '@sophocles/router/routes/support';
import Page404 from '@sophocles/views/errors/Page404';

function ApplicationRouter() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    initGoogleAnalytics();
  }, []);

  // remove any commas from the URL
  useEffect(() => {
    if (pathname.indexOf(',') !== -1) {
      navigate(pathname.replace(/,/g, ''), { replace: true });
    }

    // console.debug('tracking page view: ', pathname + search);
    trackPageView(pathname + search);
  }, [pathname]);

  return (
    <App>
      <Suspense fallback={<RouteLoader showShell />}>
        <Routes>
          {rootStackRoutes}
          {customerRoutes}
          {siteGroupRoutes}
          {siteRoutes}
          {supportRoutes}
          {billingRoutes}
          {adminRoutes}
          {salesRoutes}
          {servicesRoutes}
          <Route path="*" element={<Page404 />} />
        </Routes>
      </Suspense>
    </App>
  );
}

export default ApplicationRouter;
